/**
 * UpgradeModal - Shown when chat is blocked due to expired trial
 * Prompts user to subscribe to continue using Casey
 */

import { FC } from 'react'
import { Sparkles, Stethoscope, FileText, Brain, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { useSubscription } from '../../contexts/SubscriptionContext'

interface UpgradeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  message?: string
}

/**
 * Upgrade prompt modal displayed when subscription is required
 */
export const UpgradeModal: FC<UpgradeModalProps> = ({
  open,
  onOpenChange,
  message
}) => {
  const { upgrade, statusMessage } = useSubscription()

  const handleUpgrade = async () => {
    await upgrade()
    // Note: upgrade() redirects to Stripe, so this won't execute
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-clinical-teal/20 to-clinical-sage/20 flex items-center justify-center mb-4">
            <Sparkles className="h-8 w-8 text-clinical-teal" />
          </div>
          <DialogTitle className="text-xl font-semibold">
            {message || statusMessage || 'Subscription Required'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Subscribe to Casey's Panel Pro to continue using all features
          </DialogDescription>
        </DialogHeader>

        {/* Features list */}
        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="h-4 w-4 text-clinical-teal" />
            </div>
            <div>
              <p className="text-sm font-medium">AI Clinical Assistant</p>
              <p className="text-xs text-muted-foreground">
                Generate HPI, A&P, and handoffs with AI assistance
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-clinical-teal" />
            </div>
            <div>
              <p className="text-sm font-medium">Smart Documentation</p>
              <p className="text-xs text-muted-foreground">
                Auto-extract patient data from notes and lab results
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-clinical-teal/10 flex items-center justify-center flex-shrink-0">
              <Brain className="h-4 w-4 text-clinical-teal" />
            </div>
            <div>
              <p className="text-sm font-medium">Evidence-Based Support</p>
              <p className="text-xs text-muted-foreground">
                PubMed, drug info, and clinical decision support tools
              </p>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-surface-elevated/50 rounded-lg p-4 text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-3xl font-bold">$69.99</span>
            <span className="text-muted-foreground">/month</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Cancel anytime
          </p>
        </div>

        {/* CTA */}
        <div className="pt-4 space-y-3">
          <Button
            onClick={handleUpgrade}
            className="w-full h-11 bg-gradient-to-r from-clinical-teal to-clinical-sage hover:from-clinical-teal/90 hover:to-clinical-sage/90 text-white font-medium"
          >
            Subscribe Now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full h-9 text-muted-foreground"
          >
            Maybe Later
          </Button>
        </div>

        <p className="text-[10px] text-center text-muted-foreground mt-2">
          Secure payment via Stripe. HIPAA compliant.
        </p>
      </DialogContent>
    </Dialog>
  )
}
